import { API_ENDPOINTS } from '../constants/endpoints';
import { get, post, put, del } from '../utils/api.utils';
import { AuthResponse, ChangePasswordRequest, ConfirmSignupParams, LoginCredentials, SignupRequest, SignupResponse, updateAddonAccountRequest, UpdateProfileRequest, ValidateSessionResponse, CompanyUsersResponse, SubdomainGenerateResponse, SubdomainCheckResponse, VerifyLoginOTPCredentials, SendLoginOTPResponse, ChangePasswordAfterLoginRequest, AuthGenericResponse, CustomDomainRequest, CheckEmailExistsRequest } from '../types/auth';
import StorageService, { STORAGE_KEYS } from '../utils/storage.util';
import { decodeMetadataToken } from '../utils/token.utils';
import { validateCompanyId, sanitizeTextInput, validateAndSanitizeName } from '../utils/common.util';

class AuthService {
  public static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await post<AuthResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials);
      
      if (response.data.accessToken) {
        StorageService.setItem(STORAGE_KEYS.STATE, response.data.state);
        StorageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
        StorageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
        StorageService.setItem(STORAGE_KEYS.ID_TOKEN, response.data.idToken);
        StorageService.setItem(STORAGE_KEYS.METADATA_TOKEN, response.data.metadataToken);
      }

      return response;
    } catch (error: any) {
      if(error.response?.data?.statusCode == 418) {
        throw new Error('UserNotConfirmedException');
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid email or password');
      }
      if (error.response?.status === 400) {
        throw new Error('Please provide valid email and password');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many login attempts. Please try again later');
      }
      
      throw new Error('An error occurred during login. Please try again');
    }
  }

  public static async validateSession(): Promise<boolean> {
    try {
      const metadata = decodeMetadataToken();
      if(metadata.client_email) {
        console.log('Client session cannot be validated');
        return true
      }
      const accessToken = StorageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = StorageService.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (!accessToken || !refreshToken) {
        return false;
      }

      const response = await get<ValidateSessionResponse>(
        API_ENDPOINTS.AUTH.VALIDATE_SESSION,
        {
          headers: {
            Authorization: accessToken,
            'refresh-token': refreshToken
          }
        }
      );

      if (response.data.isValid) {
        // If new tokens were provided, update them
        if (response.data.newTokens) {
          const { accessToken, idToken, metadataToken } = response.data.newTokens;
          StorageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
          StorageService.setItem(STORAGE_KEYS.ID_TOKEN, idToken);
          StorageService.setItem(STORAGE_KEYS.METADATA_TOKEN, metadataToken);
        }
        return true;
      }

      this.logout();
      return false;
    } catch (error) {
      this.logout();
      return false;
    }
  }

  public static logout(): void {
    // todo - call backend api
    StorageService.removeItem(STORAGE_KEYS.STATE)
    StorageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    StorageService.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    StorageService.removeItem(STORAGE_KEYS.ID_TOKEN);
    StorageService.removeItem(STORAGE_KEYS.METADATA_TOKEN);
  }

  public static async changePassword(request: ChangePasswordRequest): Promise<AuthResponse> {
    try {
      const response = await post<AuthResponse>(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, request);
      return response
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Current password is incorrect');
      }
      if (error.response?.status === 400) {
        throw new Error('New password does not meet requirements');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many attempts. Please try again later');
      }
      
      throw new Error('An error occurred while changing password. Please try again');
    }
  }

  public static async updateProfile(request: UpdateProfileRequest): Promise<AuthResponse> {
    try {
      const accessToken = StorageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const response = await post<AuthResponse>(
        API_ENDPOINTS.AUTH.UPDATE_PROFILE,
        request,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );
      
      // Call refresh token if update was successful
      if (response.status === 200) {
        await this.refreshToken();
      }
      
      return response;
    } catch (error: any) {
      console.log(error)
      if (error.response?.status === 409) {
        throw new Error(error.response?.data?.message);
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid profile information provided');
      }
      else {
        throw new Error('Error updating profile');
      }
    }
  }

  public static async refreshToken(): Promise<AuthResponse> {
    try {
      const metadata = decodeMetadataToken();
      if(metadata.client_email) {
        console.log('Client session cannot be refreshed');
        return {
          data: { 
            state: '',
            accessToken: '',
            refreshToken: '',
            idToken: '',
            metadataToken: ''
          },
          message: 'Client session cannot be refreshed',
          status: 401
        }
      }
      
      const refreshToken = StorageService.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await post<AuthResponse>(
        API_ENDPOINTS.AUTH.REFRESH_TOKEN,
        { refreshToken },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        StorageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
        StorageService.setItem(STORAGE_KEYS.ID_TOKEN, response.data.idToken);
        StorageService.setItem(STORAGE_KEYS.METADATA_TOKEN, response.data.metadataToken);
        // Update refresh token if a new one is provided
        if (response.data.refreshToken) {
          StorageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
        }
      }

      return response;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Invalid refresh token');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid request');
      }
      
      throw new Error('An error occurred while refreshing token. Please login again');
    }
  }

  public static async signup(request: SignupRequest): Promise<SignupResponse> {
    try {
      // Security: Validate and sanitize input data to prevent hyperlink injection
      const firstNameValidation = validateAndSanitizeName(request.firstName, 'First name');
      if (!firstNameValidation.isValid) {
        throw new Error(firstNameValidation.error || 'Invalid first name provided');
      }
      
      const lastNameValidation = validateAndSanitizeName(request.lastName || '', 'Last name', false);
      if (!lastNameValidation.isValid) {
        throw new Error(lastNameValidation.error || 'Invalid last name provided');
      }
      
      // Create sanitized request object
      const sanitizedRequest: SignupRequest = {
        ...request,
        firstName: firstNameValidation.sanitizedValue || '',
        lastName: lastNameValidation.sanitizedValue || '',
        email: request.email?.trim().toLowerCase() || '',
        phoneNumber: sanitizeTextInput(request.phoneNumber || '')
      };
      
      const response = await post<SignupResponse>(API_ENDPOINTS.AUTH.SIGNUP, sanitizedRequest);

      return response;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('User already exists with this email');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid signup information provided');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many signup attempts. Please try again later');
      }
      
      // Re-throw validation errors
      if (error.message && (error.message.includes('contains') || error.message.includes('Invalid'))) {
        throw error;
      }
      
      throw new Error('An error occurred during signup. Please try again');
    }
  }

  public static async getTokensFromCode(code: string, isRegistration: boolean, isTrialPlan: boolean, utmParams?: string): Promise<AuthResponse> {
    try {
      const response = await post<AuthResponse>(
        `${API_ENDPOINTS.AUTH.TOKENS}/${code}/${isRegistration}/${isTrialPlan}`,
        { utm_params: utmParams }
      );
      
      if (response.data) {
        StorageService.setItem(STORAGE_KEYS.STATE, response.data.state);
        StorageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
        StorageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
        StorageService.setItem(STORAGE_KEYS.ID_TOKEN, response.data.idToken);
        StorageService.setItem(STORAGE_KEYS.METADATA_TOKEN, response.data.metadataToken);
      }

      return response;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Invalid or expired code');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid request');
      }
      
      throw new Error('An error occurred while retrieving tokens. Please try again');
    }
  }

  public static async confirmSignup(
    params: ConfirmSignupParams
  ): Promise<AuthResponse> {
    try {
      const response = await post<AuthResponse>(API_ENDPOINTS.AUTH.CONFIRM_SIGNUP, {
        email: params.email,
        confirmationCode: params.confirmationCode,
        password: params.password,
        inf_lead_squared_id: params.infLeadSquaredId,
        companyId: params.companyId,
        companyName: params.companyName
      });

      if(response?.data?.accessToken) {
        StorageService.setItem(STORAGE_KEYS.STATE, response.data.state);
        StorageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
        StorageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
        StorageService.setItem(STORAGE_KEYS.ID_TOKEN, response.data.idToken);
        StorageService.setItem(STORAGE_KEYS.METADATA_TOKEN, response.data.metadataToken);
      }

      return response;
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 400) {
        throw new Error('Invalid confirmation code');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many confirmation attempts. Please try again later');
      }
      
      throw new Error('An error occurred during signup confirmation. Please try again');
    }
  }

  public static async resendConfirmationCode(email: string): Promise<void> {
    try {
      await post(API_ENDPOINTS.AUTH.RESEND_CONFIRMATION, { email });
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid email format');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many resend attempts. Please try again later');
      }
      
      throw new Error('An error occurred while resending confirmation code. Please try again');
    }
  }

  public static async getCompaniesByParentId(parentCompanyId: string): Promise<any> {
    try {
      const response = await get(
        `${API_ENDPOINTS.AUTH.COMPANIES}/by-parent/${parentCompanyId}`
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Parent company not found');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid parent company ID');
      }
      
      throw new Error('An error occurred while fetching companies. Please try again');
    }
  }

  public static async updateAddonAccounts(request: updateAddonAccountRequest): Promise<void> {
    try {
      await put(
        API_ENDPOINTS.AUTH.UPDATE_ADDON_ACCOUNTS,
        request
      );
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid account information provided');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      
      throw new Error('An error occurred while updating addon account. Please try again');
    }
  }

  public static async getCompanyUsers(companyId: string): Promise<CompanyUsersResponse> {
    try {
      //Security: Validate companyId parameter using utility function
      const companyIdValidation = validateCompanyId(companyId);
      if (!companyIdValidation.isValid) {
        throw new Error(companyIdValidation.error);
      }
      
      const accessToken = StorageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (!accessToken) {
        throw new Error('No access token found. Please login again');
      }
      
      const response = await get<CompanyUsersResponse>(
        `${API_ENDPOINTS.AUTH.COMPANIES}/${companyId}/users`
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Company not found or you do not have access to this company');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      if (error.response?.status === 403) {
        throw new Error('Access denied. You do not have permission to view users for this company');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid company ID or request format');
      }
      
      // Security: Don't expose internal error details
      console.error('Error fetching company users:', error);
      throw new Error('An error occurred while fetching company users. Please try again');
    }
  }

  public static async forgotPassword(email: string): Promise<void> {
    try {
      await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid email format');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many requests. Please try again later');
      }
      
      throw new Error('An error occurred while processing forgot password request. Please try again');
    }
  }

  public static async confirmForgotPassword(email: string, confirmationCode: string, newPassword: string): Promise<void> {
    try {
      await post(API_ENDPOINTS.AUTH.CONFIRM_FORGOT_PASSWORD, {
        email,
        confirmationCode,
        newPassword
      });
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid request format or password requirements not met');
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid confirmation code');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many attempts. Please try again later');
      }
      
      throw new Error('An error occurred while confirming password reset. Please try again');
    }
  }

  public static async generateSubdomain(baseSubdomain: string, application: string): Promise<SubdomainGenerateResponse> {
    try {
      const response = await post<{ data: SubdomainGenerateResponse }>(
        API_ENDPOINTS.AUTH.GENERATE_SUBDOMAIN,
        { baseSubdomain, application }
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid subdomain format or application');
      }
      if (error.response?.status === 409) {
        throw new Error('Subdomain already exists');
      }
      
      throw new Error('An error occurred while generating subdomain. Please try again');
    }
  }

  public static async checkSubDomainAvailability(subdomain: string, application: string): Promise<SubdomainCheckResponse> {
    try {
      const response = await get<{ data: SubdomainCheckResponse }>(
        `${API_ENDPOINTS.AUTH.CHECK_SUBDOMAIN}?subdomain=${subdomain}&application=${application}`
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid subdomain format or application');
      }
      if (error.response?.status === 404) {
        throw new Error('Subdomain not found');
      }
      
      throw new Error('An error occurred while checking subdomain. Please try again');
    }
  }

  public static async updateCompanyDomain(companyId: string, domain: string, application: string) {
    try {
      const response = await put<{ data: { success: boolean; message: string; domain: string } }>(
        `${API_ENDPOINTS.AUTH.COMPANIES}/${companyId}/domain`,
        { domain, application }
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid domain format or application');
      }
      if (error.response?.status === 404) {
        throw new Error('Company not found');
      }
      if (error.response?.status === 409) {
        throw new Error('Domain is already in use');
      }
      
      throw new Error('An error occurred while updating company domain. Please try again');
    }
  }

  public static async uploadProfilePhoto(formData: FormData): Promise<any> {
    try {
      // Validate file type and size
      const file = formData.get('file') as File;
      if (file) {
        // Check file type
        if (!file.type.startsWith('image/')) {
          throw new Error('Invalid file type. Please upload an image file');
        }
        
        // Check file size (5MB limit)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          throw new Error('File size too large. Please choose a smaller image (max 5MB)');
        }
      }

      const accessToken = StorageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      
      const response = await post(
        API_ENDPOINTS.AUTH.UPLOAD_PROFILE_PHOTO, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${accessToken}`
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            console.log('Upload Progress:', percentCompleted);
          }
        }
      );

      return response;
    } catch (error: any) {
      if (error.response?.status === 413) {
        throw new Error('File size too large. Please choose a smaller image');
      }
      if (error.response?.status === 415) {
        throw new Error('Invalid file type. Please upload an image file');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      
      // If it's our custom validation error, throw it directly
      if (error.message && (error.message.includes('Invalid file type') || error.message.includes('File size too large'))) {
        throw error;
      }
      
      throw new Error('An error occurred while uploading profile photo. Please try again');
    }
  }

  public static async sendLoginOTP(email: String): Promise<SendLoginOTPResponse> {
    try {
      const response = await post<SendLoginOTPResponse>(API_ENDPOINTS.AUTH.SEND_LOGIN_OTP, {
        email
      });
      return response
    } catch (error: any) {      
      throw new Error('An error occurred while sending otp. Please try again');
    }
  }

  public static async verifyLoginOTP(credentials: VerifyLoginOTPCredentials): Promise<AuthResponse> {
    try {
      const response = await post<AuthResponse>(API_ENDPOINTS.AUTH.VERIFY_LOGIN_OTP, credentials);
      
      if (response.data) {
        StorageService.setItem(STORAGE_KEYS.STATE, response.data.state);
        StorageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
        StorageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
        StorageService.setItem(STORAGE_KEYS.ID_TOKEN, response.data.idToken);
        StorageService.setItem(STORAGE_KEYS.METADATA_TOKEN, response.data.metadataToken);
      }

      return response;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Invalid email');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many login attempts. Please try again later');
      }
      
      throw new Error('An error occurred during login. Please try again');
    }
  }

  public static async changePasswordAfterLogin(request: ChangePasswordAfterLoginRequest): Promise<AuthGenericResponse> {
    try {
      const response = await post<AuthGenericResponse>(
        API_ENDPOINTS.AUTH.CHANGE_PASSWORD_AFTER_LOGIN,
        request
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Current password is incorrect or unauthorized access');
      }
      if (error.response?.status === 400) {
        throw new Error('New password does not meet requirements');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many attempts. Please try again later');
      }

      if (error.response?.status === 418) {
        throw new Error('Please confirm account before changing password');
      }
      
      throw new Error('An error occurred while changing password. Please try again');
    }
  }

  public static async deleteWorkspace(workspaceId: string): Promise<void> {
    try {
      await del(
        `${API_ENDPOINTS.AUTH.WORKSPACE}/${workspaceId}`
      );
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to delete this workspace');
      }
      if (error.response?.status === 404) {
        throw new Error('Workspace not found');
      }
      
      throw new Error('An error occurred while deleting workspace. Please try again');
    }
  }

  public static async updateCompanyCustomDomain(companyId: string, customDomain: string, customDomainStatus: string, cognitoUserId: string, subDomain: string): Promise<any> {
    try {
      const requestData: CustomDomainRequest = {
        customDomain,
        customDomainStatus,
        cognitoUserId,
        subDomain
      };
      
      const response = await put(
        `${API_ENDPOINTS.AUTH.CUSTOM_DOMAIN}/${companyId}/custom-domain`,
        requestData
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid request parameters');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to update this company');
      }
      if (error.response?.status === 404) {
        throw new Error('Company not found');
      }
      
      throw new Error('An error occurred while updating company custom domain. Please try again');
    }
  }

  public static async confirmMFA(email: string, mfaCode: string, challengeName: string, session: string): Promise<AuthResponse> {
    try {
      const response = await post<AuthResponse>(API_ENDPOINTS.AUTH.CONFIRM_MFA, {
        email,
        mfaCode,
        challengeName,
        session
      });
      
      if (response.data) {
        StorageService.setItem(STORAGE_KEYS.STATE, response.data.state);
        StorageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken);
        StorageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken);
        StorageService.setItem(STORAGE_KEYS.ID_TOKEN, response.data.idToken);
        StorageService.setItem(STORAGE_KEYS.METADATA_TOKEN, response.data.metadataToken);
      }

      return response;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Invalid MFA code');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid request format');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many verification attempts. Please try again later');
      }
      
      throw new Error('An error occurred during MFA verification. Please try again');
    }
  }

  public static async linkCognitoAccounts(email: string, cognitoUserId: string): Promise<AuthGenericResponse> {
    try {
      const response = await post<AuthGenericResponse>(
        API_ENDPOINTS.AUTH.LINK_COGNITO_ACCOUNTS,
        { email, cognitoUserId }
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid request parameters');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (error.response?.status === 409) {
        throw new Error('User account is already linked');
      }
      
      throw new Error('An error occurred while linking accounts. Please try again');
    }
  }

  public static async deleteCognitoUser(userId: string): Promise<AuthGenericResponse> {
    try {
      const response = await del<AuthGenericResponse>(
        API_ENDPOINTS.AUTH.DELETE_COGNITO_USER,
        { data: { userId } }
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid user ID');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to delete this user');
      }
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      
      throw new Error('An error occurred while deleting user. Please try again');
    }
  }

  public static async getEasyWebinarTimezones(): Promise<AuthGenericResponse> {
    try {
      const response = await get<AuthGenericResponse>(
        API_ENDPOINTS.AUTH.EASYWEBINAR_TIMEZONES
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please login again');
      }
      if (error.response?.status === 404) {
        throw new Error('Timezones not found');
      }
      
      throw new Error('An error occurred while fetching EasyWebinar timezones. Please try again');
    }
  }

  public static async checkEmailExists(email: string): Promise<AuthGenericResponse> {
    try {
      const requestData: CheckEmailExistsRequest = { email };
      
      const response = await post<AuthGenericResponse>(
        API_ENDPOINTS.AUTH.CHECK_EMAIL_EXISTS,
        requestData
      );
      return response;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid email format');
      }
      if (error.response?.status === 500) {
        throw new Error('Internal server error. Please try again later');
      }
      
      throw new Error('An error occurred while checking email existence. Please try again');
    }
  }
}

export default AuthService;